/**
 * orders.ts — checkout, dashboard and delivery-address calls.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: post-decision checkout. Signed-in PATIENT uses Bearer session. Guests use
 * assessment-bound `checkoutToken` on `/checkout/guest/*` (pay before account),
 * then `claimCheckout` after register/login.
 */
import { get, patch, post, put, queryString, request } from './client';
import type {
  Address,
  ApproveResult,
  AttachPaymentMethodRequest,
  RetryCardRequest,
  AttachPaymentMethodResult,
  AuditListResponse,
  AuditQuery,
  CaseNote,
  CaseNoteList,
  CheckoutRequest,
  CheckoutResume,
  CheckoutSelectionRequest,
  CheckoutSelectionResult,
  CheckoutSessionResult,
  DashboardData,
  PaymentFailureList,
  PaymentLinkResult,
  PlanPreview,
  PrescribingContext,
  PrescriptionIssueRequest,
  PrescriptionResult,
  RejectResult,
  StaffOrder,
  StaffPrescriptionsResponse,
  StaffSubscriptionDetail,
  PaymentsPipelineResponse,
  StaffSubscriptionsResponse,
  ActivityHeatmap,
  ClinicianOrderStats,
  ClinicianCompensationRow,
  CompensationList,
  CompensationRates,
  EarningsResponse,
  MyEarningsResponse,
  ResubscribeConfirmResult,
  ResubscribeDeclineResult,
  ResubscribeInitiateRequest,
  ResubscribeInitiateResult,
  ResubscribeOffer,
  ResubscribeOfferList,
  ResubscribeOfferPreview,
  ResubscribeOfferState,
  ResubscribeSelfRequest,
  ResubscribeTokenRequest,
  OrderHoldStatus,
  OrderMarkTestResult,
  PhotoReviewStatus,
} from './types';

function guestHeaders(checkoutToken: string, assessmentId?: string): Record<string, string> {
  const headers: Record<string, string> = { 'x-checkout-token': checkoutToken };
  if (assessmentId) headers['x-assessment-id'] = assessmentId;
  return headers;
}

/**
 * POST /v1/orders/checkout/session → OrderCase + dormant Subscription + SetupIntent
 * grant. Does NOT create a hold. Amount / capture mode are never sent by the browser.
 */
export function checkoutSession(
  body: CheckoutRequest,
  opts?: { checkoutToken?: string },
): Promise<CheckoutSessionResult> {
  if (opts?.checkoutToken) {
    return post('/v1/orders/checkout/guest/session', body, {
      parseError: true,
      headers: guestHeaders(opts.checkoutToken, body.assessmentId),
    });
  }
  return post('/v1/orders/checkout/session', body, { auth: true, parseError: true });
}

/**
 * POST /v1/orders/checkout/selection — persist plan after Plan → Address.
 * No address / SetupIntent; server quotes price. Idempotent per assessment.
 */
export function saveCheckoutSelection(
  body: CheckoutSelectionRequest,
  opts?: { checkoutToken?: string },
): Promise<CheckoutSelectionResult> {
  if (opts?.checkoutToken) {
    return post('/v1/orders/checkout/guest/selection', body, {
      parseError: true,
      headers: guestHeaders(opts.checkoutToken, body.assessmentId),
    });
  }
  return post('/v1/orders/checkout/selection', body, { auth: true, parseError: true });
}

/**
 * GET /v1/orders/checkout/resume/:assessmentId — restore Payment/confirm after
 * hard refresh. Soft status:'none' when nothing to resume.
 */
export function getCheckoutResume(
  assessmentId: string,
  opts?: { checkoutToken?: string },
): Promise<CheckoutResume> {
  if (opts?.checkoutToken) {
    return request(`/v1/orders/checkout/guest/resume/${encodeURIComponent(assessmentId)}`, {
      method: 'GET',
      parseError: true,
      headers: guestHeaders(opts.checkoutToken, assessmentId),
    });
  }
  return get(`/v1/orders/checkout/resume/${encodeURIComponent(assessmentId)}`, true);
}

/**
 * POST /v1/orders/checkout/:orderCaseId/payment-method → after Stripe.js confirms
 * SetupIntent (or FakeStripe stub confirm), Orders creates the hold/capture.
 * Body is `{ setupIntentId }` only.
 */
export function attachPaymentMethod(
  orderCaseId: string,
  body: AttachPaymentMethodRequest,
  opts?: { checkoutToken?: string; assessmentId?: string },
): Promise<AttachPaymentMethodResult> {
  if (opts?.checkoutToken) {
    return post(`/v1/orders/checkout/guest/${encodeURIComponent(orderCaseId)}/payment-method`, body, {
      parseError: true,
      headers: guestHeaders(opts.checkoutToken, opts.assessmentId),
    });
  }
  return post(`/v1/orders/checkout/${encodeURIComponent(orderCaseId)}/payment-method`, body, {
    auth: true,
    parseError: true,
  });
}

/**
 * POST /v1/orders/checkout/:orderCaseId/retry-card → a NEW card session after the
 * bank DECLINED the month-1 hold (2026-09-25). Body `{ setupIntentId }`: the
 * intent the declined attach presented. orders rotates the case onto a new
 * SetupIntent (only after a decline it recorded itself) and answers the
 * `checkout/session` shape. Moves no money. The guest lane carries the grant and
 * the assessment id in headers, like the guest attach — orders' body is strict.
 */
export function retryCardAfterDecline(
  orderCaseId: string,
  body: RetryCardRequest,
  opts?: { checkoutToken?: string; assessmentId?: string },
): Promise<CheckoutSessionResult> {
  if (opts?.checkoutToken) {
    return post(`/v1/orders/checkout/guest/${encodeURIComponent(orderCaseId)}/retry-card`, body, {
      parseError: true,
      headers: guestHeaders(opts.checkoutToken, opts.assessmentId),
    });
  }
  return post(`/v1/orders/checkout/${encodeURIComponent(orderCaseId)}/retry-card`, body, {
    auth: true,
    parseError: true,
  });
}

/**
 * PATIENT — bind guest OrderCase to the signed-in account after register/login.
 */
export function claimCheckout(body: {
  assessmentId: string;
  checkoutToken?: string;
}): Promise<{ orderCaseId: string; claimed: boolean }> {
  return post('/v1/orders/checkout/claim', body, { auth: true, parseError: true });
}

/**
 * GET /v1/orders/plan-preview/:assessmentId (2026-07-20) → has a clinician
 * already built + pre-approved a plan for this assessment? When it has, the
 * checkout skips plan-selection for a read-only summary and paying captures
 * immediately (no separate approval click).
 */
export function getPlanPreview(
  assessmentId: string,
  opts?: { checkoutToken?: string },
): Promise<PlanPreview> {
  if (opts?.checkoutToken) {
    return request(`/v1/orders/checkout/guest/plan-preview/${encodeURIComponent(assessmentId)}`, {
      method: 'GET',
      parseError: true,
      headers: guestHeaders(opts.checkoutToken, assessmentId),
    });
  }
  return get(`/v1/orders/plan-preview/${encodeURIComponent(assessmentId)}`, true);
}

/** GET /v1/orders/me/dashboard → current plan, orders, bills. */
export function getDashboard(): Promise<DashboardData> {
  return get('/v1/orders/me/dashboard', true);
}

/**
 * PATCH /v1/orders/:id/delivery-address — editable until approval. The gateway
 * returns 409 once the order is approved; callers surface that as "locked".
 */
export function updateDeliveryAddress(orderCaseId: string, address: Address): Promise<{ ok?: boolean }> {
  return patch(`/v1/orders/${encodeURIComponent(orderCaseId)}/delivery-address`, address);
}

/* ── Staff queue + review actions (CLINICIAN / ADMIN) ───────────────────────
 *
 * MONEY-REDACTION (Reshape §3.1): GET /v1/orders is the staff queue; the gateway
 * → orders-service strips ALL money for a CLINICIAN and keeps `monthlyPence` for
 * an ADMIN. The clinician surface additionally never reads or renders money.
 */

/** GET /v1/orders → the staff order queue (money-redacted for CLINICIAN). */
export function getStaffQueue(): Promise<StaffOrder[]> {
  return get('/v1/orders', true);
}

/**
 * POST /v1/orders/:id/approve → money-free activation summary (approval-gated capture).
 *
 * `parseError` for the SAME reason `markPhotosReviewed` below has it: approve
 * is the other end of the photos-reviewed gate, and its refusal
 * (PhotosNotReviewedError, 409) is the one message that tells a clinician what
 * to actually do — confirm the photos first. Without it the refusal arrived as
 * the generic "that action could not be completed", which is indistinguishable
 * from a network blip and sends the clinician nowhere. The gate's read can fail
 * while orders-service is healthy, so this 409 is a path a clinician really
 * meets rather than a theoretical one.
 *
 * PHI (R19): the orders-service refusal bodies on this route are order ids plus
 * a state sentence — no patient data by construction — which is the same
 * standard the attestation route is held to. 409 is already in the client's
 * parse list.
 */
export function approveOrder(orderCaseId: string): Promise<ApproveResult> {
  return post(`/v1/orders/${encodeURIComponent(orderCaseId)}/approve`, {}, { auth: true, parseError: true });
}

/**
 * POST /v1/orders/:id/photos-reviewed → the CLINICIAN attestation that the
 * patient's submitted photos were reviewed (2026-08-27). This is what clears
 * the photos-reviewed-before-money gate in approve; the server enforces the
 * role and the gate, the portal only mirrors the affordance (R41).
 *
 * Write-once and idempotent — a re-post returns the ORIGINAL stamp rather than
 * shifting the attested moment or reassigning who attested.
 *
 * `parseError` because the TWO 409s must reach the clinician VERBATIM, and
 * they mean completely different things: no photo evidence exists at all
 * (the patient never uploaded — nothing to review) versus the evidence read
 * could not be verified (the clinic service could not answer — retryable).
 * Collapsing them into one "could not attest" would send a clinician chasing
 * a patient who has already uploaded, or waiting on one who has not. Both
 * bodies are ids + a state sentence — PHI-free by construction (R19).
 */
export function markPhotosReviewed(orderCaseId: string): Promise<PhotoReviewStatus> {
  return post(
    `/v1/orders/${encodeURIComponent(orderCaseId)}/photos-reviewed`,
    {},
    { auth: true, parseError: true },
  );
}

/**
 * GET /v1/orders/:id/photo-review-status → whether the photos-reviewed gate
 * applies to this order and, when attested, when and by whom (CLINICIAN and
 * ADMIN — reading is not attesting). Money-free by shape, so it is safe on the
 * clinician surface; it is what the approve control's affordance keys on.
 */
export function getPhotoReviewStatus(orderCaseId: string): Promise<PhotoReviewStatus> {
  return get(`/v1/orders/${encodeURIComponent(orderCaseId)}/photo-review-status`, true);
}

/**
 * GET /v1/orders/:id/hold-status → whether a card is on file and whether the
 * month-1 pre-authorisation is still valid (orders-service #233, CTO
 * 2026-09-11). orders-service asks STRIPE, live, which is why the deal drawer
 * calls this when it opens instead of trusting the board's ledger posture.
 *
 * CLINICIAN and ADMIN. Money is role-shaped on the server — a clinician's answer
 * has no `authorisedPence` key — and `HoldStatusPanel` re-checks the role before
 * it prints a figure.
 *
 * No `parseError`, deliberately: a 404 (unknown case), a 5xx and a network
 * fault all render the same "unavailable" line, and there is no refusal
 * sentence here a desk could act on. What matters is that a failure is never
 * read as "no card".
 */
export function getOrderHoldStatus(orderCaseId: string): Promise<OrderHoldStatus> {
  return get(`/v1/orders/${encodeURIComponent(orderCaseId)}/hold-status`, true);
}

/**
 * POST /v1/orders/:id/reject → releases the hold, marks the case rejected.
 * The body's REQUIRED `reason` (3..2000 chars, G2 pinned wire change) is
 * recorded on the server-side audit row — it never appears in an event/log.
 */
export function rejectOrder(orderCaseId: string, reason: string): Promise<RejectResult> {
  return post(`/v1/orders/${encodeURIComponent(orderCaseId)}/reject`, { reason }, { auth: true });
}

/**
 * POST /v1/orders/:id/prescribe → issue the prescription for an APPROVED order.
 * Approval-gated server-side (a non-approved case throws first); the UI only
 * enables this after approval to reflect that gate.
 *
 * `parseError` since 2026-09-10, and this one was earned the hard way. SIX
 * distinct refusals land on this route — already prescribed and sent, payment
 * not captured, prescriber not identifiable, weight evidence outstanding,
 * dispense already claimed, order not approved — and every one of them reached
 * the clinician as the same twelve words: "Sorry — that action could not be
 * completed. Please try again." On production a clinician pressed Prescribe on
 * an order whose prescription had ALREADY gone to the pharmacy 36 seconds
 * earlier, was told to try again, and did — twice, 20 seconds apart, both 409.
 *
 * ⚠️ "Please try again" is not merely unhelpful on these, it is WRONG: a 409
 * here means the order's STATE refuses the action, so an identical retry
 * refuses identically (orders-service says so itself in `isGateRefusal`). The
 * server's own sentence is on the wire and the gateway relays it verbatim.
 *
 * R19: these bodies are order ids plus a state sentence — no patient data by
 * construction — which is the same standard `approveOrder` and
 * `markPhotosReviewed` above are already held to.
 */
export function prescribe(orderCaseId: string, body: PrescriptionIssueRequest): Promise<PrescriptionResult> {
  return post(`/v1/orders/${encodeURIComponent(orderCaseId)}/prescribe`, body, { auth: true, parseError: true });
}

/**
 * GET /v1/orders/:orderCaseId/prescribing-context → what the prescribe form
 * needs beyond the medication lines (2026-09-24): the supplies CATALOGUE, the
 * supplies the server SUGGESTS for this order and why, and whether this is the
 * patient's first prescription. CLINICIAN/ADMIN, money-free.
 *
 * Source: orders-service's local strict schema (a `[CONTRACTS GAP]` — there is
 * no contracts DTO for it; the mirror is `PrescribingContext` in `api/types.ts`).
 *
 * WHY THE SUGGESTION RULE IS NOT IN THIS REPO. "The pen supply with every
 * injectable treatment, a sharps bin on a first prescription" is the clinical
 * lead's rule (R28 keeps the supply's own name out of this repo), and it is
 * applied ONCE, server-side, from the catalogue's own `suggestWith` field (R41).
 * A copy here would be a second answer that drifts — and the browser cannot
 * even see the two facts it keys on reliably (the family's dose FORM and the
 * patient's prescription history across every order case).
 *
 * ⚠️ A FAILED READ BLOCKS THE ISSUE CONTROL, like a failed formulary read. The
 * incident this exists for is a supply that silently did not go out; a form
 * that quietly offered "no supplies" during an outage would reproduce it.
 */
export function getPrescribingContext(orderCaseId: string): Promise<PrescribingContext> {
  return get(`/v1/orders/${encodeURIComponent(orderCaseId)}/prescribing-context`, true);
}

/**
 * GET /v1/orders/:orderCaseId/prescriptions → every prescription issued for
 * this case, newest first (money-free — CLINICIAN/ADMIN). Powers the "Previously
 * issued prescriptions" panel on the review.
 */
export function getCasePrescriptions(orderCaseId: string): Promise<StaffPrescriptionsResponse> {
  return get(`/v1/orders/${encodeURIComponent(orderCaseId)}/prescriptions`, true);
}

/* ── THE MEDICATION CORRECTION LANE — DELETED 2026-09-12 ───────────────────
 *
 * `amendMedication()` POSTed `/v1/orders/order-cases/:id/medication-amendment`.
 * orders-service deleted that controller in the same slice that deleted the
 * month-1 auto-issue, and this client kept calling it: the button was mounted
 * for every non-admin clinician on every case with a prescription, so the one
 * screen built for this incident answered 404. A fixture stubbing the route 200
 * is why no test noticed — it is gone too.
 *
 * NOTHING REPLACES IT AS A ROUTE. Re-prescribing an order whose strength was
 * never recorded goes through `prescribe()` above — the ordinary lane, with the
 * gates it already carries. See `clinicians/RePrescribeModal.tsx`.
 */

/**
 * POST /v1/orders/:orderCaseId/mark-test — flag the order case as a TEST
 * record (2026-08-26, reporting hygiene — the orders half of the deal
 * drawer's "Mark as test record", fired FIRST: the deal half closes and
 * hides the deal, so order-first means a partial failure can never leave a
 * hidden deal with a still-"real" order on the admin money boards). The
 * server sets `isTest` so money/order reporting excludes the case; nothing
 * is captured, released or refunded — the flag is bookkeeping, never a money
 * action. The server 409s while the case still has an ACTIVE subscription
 * (a live billing schedule is never test data) — `parseError` lifts that
 * PHI-free message into ApiError.detail so the drawer can show it VERBATIM:
 * staff must cancel the sub first, and only the server's message says so.
 * `reason` is OPTIONAL and lands on the server-side audit row only (R19).
 * The portal only mirrors affordance — role checks are server-enforced
 * (R41).
 */
export function markOrderTest(orderCaseId: string, reason?: string): Promise<OrderMarkTestResult> {
  return post(
    `/v1/orders/${encodeURIComponent(orderCaseId)}/mark-test`,
    reason ? { reason } : {},
    { auth: true, parseError: true },
  );
}

/**
 * GET /v1/orders/admin/failed-payments[?includeTest=true] → failed renewal
 * captures / at-risk + lapsed holds (ADMIN).
 *
 * `includeTest` is the Payments LIST view's reveal (2026-09-11): a staff-marked
 * test order's failures are left off the default read, and this adds them back,
 * each alert carrying `isTest`. Same wire rule as `getPaymentsPipeline` below —
 * the LITERAL `'true'` or no param at all, because orders-service 400s any other
 * value and that would take the whole feed down.
 */
export function getFailedPayments(includeTest = false): Promise<PaymentFailureList> {
  return get(`/v1/orders/admin/failed-payments${includeTest ? '?includeTest=true' : ''}`, true);
}

/**
 * POST /v1/orders/:id/payment-link → mint the Stripe-hosted payment page for
 * an order case and email it to the patient (ADMIN ONLY, server-enforced).
 *
 * `orderCaseId` is the OrderCase UUID (the PK), never the `ORD-` display ref.
 * Omit `amountPence` to bill the order's own `monthlyPence` — the server 400s
 * an omitted amount on an order that has no monthly price. The body is strict,
 * so an absent amount must serialise to a bare `{}` (an explicit
 * `amountPence: undefined` would too, but the branch says so at the call site).
 *
 * THE RESPONSE IS 200 EVEN WHEN THE EMAIL FAILED — read `emailOutcome`, never
 * the status code (see PaymentLinkResult). `parseError` so the genuine throws
 * (404 unknown order, 400 no-amount) reach the UI as a sentence rather than
 * "something went wrong".
 *
 * Safe to call twice: the Stripe mint is keyed `payment-link:{orderCaseId}` so
 * the SAME url returns, and the email is keyed on the OrderCase id so a second
 * call answers `already_sent` instead of emailing again. The caller still owns
 * a click guard, for UX rather than correctness.
 */
/**
 * What POST /v1/orders/:id/pharmacy-link answers (HOTFIX 2026-09-11).
 *
 * `minted` carries the sign-in URL and when it stops working (null only if the
 * issuer left it out); `stub` means the
 * environment has no mint configured, so nothing was created — said plainly
 * rather than shown as a failure.
 */
export type PharmacySignInLinkResult =
  | { outcome: 'minted'; authUrl: string; expiresAt: string | null }
  | { outcome: 'stub' };

/**
 * POST /v1/orders/:id/pharmacy-link — ADMIN: a FRESH pharmacy sign-in link for
 * a paid order that has already been sent to the pharmacy (HOTFIX 2026-09-11).
 *
 * WHY THIS EXISTS: the dispensing console's sign-in link is single-use and
 * expires after an hour, so the one in the dispense email (or copied into a
 * handbook) is routinely dead by the time the pharmacy opens it — and then a
 * paid order cannot be dispensed. Staff had no way to issue another.
 *
 * `parseError` so the server's own refusals (a test order, an order not yet
 * sent to the pharmacy) reach the admin as a sentence.
 */
export function issuePharmacySignInLink(orderCaseId: string): Promise<PharmacySignInLinkResult> {
  return post(`/v1/orders/${encodeURIComponent(orderCaseId)}/pharmacy-link`, {}, { auth: true, parseError: true });
}

/**
 * What a recall sweep did, in counts only (R19 — no ids, no medication, no
 * patient). `recalled` is the number of orders that ACTUALLY moved out of the
 * pharmacy queue on this run; a re-post moves nothing and answers 0.
 */
export interface PharmacyRecallResult {
  recalled: number;
}

/**
 * POST /v1/orders/admin/pharmacy/recall-unvouched — ADMIN: pull every order
 * still sitting in the external pharmacy's work queue with no prescriber-chosen
 * strength on it back into the clinician lane (`awaiting_dispense` → `approved`).
 *
 * WHY IT IS A ROUTE, AND WHY IT NEEDED A CALLER. The v1 backlog was sent to
 * Pharmacy Express before the prescribing gate existed, so no NEW send is ever
 * attempted for it and the automatic recall inside the send refusal never fires.
 * Somebody has to DECIDE to pull those orders back — which is why orders-service
 * made it an ADMIN act with a named actor on every audit row, rather than a
 * boot-time backfill attributed to a process. But the route had existed since
 * 2026-09-12 with nothing in this portal calling it, so the only way to perform
 * the decision was a curl with an admin bearer token: audited, and unreachable
 * by the people who take the phone call. This is the call.
 *
 * IDEMPOTENT SERVER-SIDE. The move is a conditional updateMany on the same
 * predicate the sweep selected on, so a second post moves nothing, writes no
 * audit rows and returns `{ recalled: 0 }`. That makes zero the NORMAL answer,
 * not a failure — see admin/RecallUnvouchedAction.tsx for how it is worded.
 *
 * `parseError` so a server refusal reaches the admin as a sentence rather than
 * as "something went wrong".
 */
export function recallUnvouchedFromPharmacy(): Promise<PharmacyRecallResult> {
  return post('/v1/orders/admin/pharmacy/recall-unvouched', {}, { auth: true, parseError: true });
}

export function createPaymentLink(orderCaseId: string, amountPence?: number): Promise<PaymentLinkResult> {
  return post(
    `/v1/orders/${encodeURIComponent(orderCaseId)}/payment-link`,
    amountPence === undefined ? {} : { amountPence },
    { auth: true, parseError: true },
  );
}

/**
 * GET /v1/orders/admin/subscriptions[?includeTest=true] → every subscription +
 * its payment ledger (ADMIN).
 *
 * `includeTest` adds the staff-marked test records the default read leaves out
 * (2026-09-11), each item carrying `isTest`. It backs the list view's
 * subscriptions table, its "No subscription yet" exclusion set and the recent-
 * payments ledger, so all three follow the one filter. Literal `'true'` or
 * omitted — see `getPaymentsPipeline`.
 */
export function getAdminSubscriptions(includeTest = false): Promise<StaffSubscriptionsResponse> {
  return get(`/v1/orders/admin/subscriptions${includeTest ? '?includeTest=true' : ''}`, true);
}

/**
 * GET /v1/orders/admin/payments-pipeline[?includeTest=true] → the four-column
 * money board (ADMIN — money, never clinician-fetched).
 *
 * `includeTest` is the opt-in AUDIT view: staff-marked QA/marketing dry runs are
 * excluded from the board by default (their pence would overstate every rollup),
 * and this widens the read to include them, each card carrying `isTest`.
 *
 * ⚠️ THE LITERAL STRING `'true'`, OR THE PARAM IS NOT SENT AT ALL. orders-service
 * pins the value at the boundary — `false`, `TRUE`, `1` and empty are each a 400,
 * which would take the whole board down rather than degrade to the default view.
 * A `boolean` argument is therefore never stringified onto the wire: false omits
 * the param, which is what the server treats as "excluded" anyway, so the normal
 * read has exactly one shape and a request diff can only show the deliberate
 * opt-in. Same rule, same reason, as identity's `includeTest` on the patients
 * directory (see `AdminPatientsQuery` in api/types.ts).
 */
export function getPaymentsPipeline(includeTest = false): Promise<PaymentsPipelineResponse> {
  return get(`/v1/orders/admin/payments-pipeline${includeTest ? '?includeTest=true' : ''}`, true);
}

/* ── Money-free analytics (CLINICIAN·ADMIN, 2026-07-21) ───────────────────────
 * COUNT reads over the orders audit trail — no money, safe on the clinician
 * surface. See orders-service src/analytics/.
 */

/** GET /v1/orders/analytics/me → my order-decision counts + daily trend (money-free). */
export function getMyOrderStats(days?: number): Promise<ClinicianOrderStats> {
  return get(`/v1/orders/analytics/me${queryString({ days })}`, true);
}

/** GET /v1/orders/analytics/activity → busiest-times heatmap over order decisions (money-free). */
export function getOrderActivity(days?: number): Promise<ActivityHeatmap> {
  return get(`/v1/orders/analytics/activity${queryString({ days })}`, true);
}

/* ── Admin compensation + earnings (ADMIN money surface, 2026-07-21) ──────────
 * Money — ADMIN-only; clinicians never see these (Reshape §3.1).
 */

/** GET /v1/orders/admin/compensation → every clinician's configured pence rates (ADMIN). */
export function getCompensation(): Promise<CompensationList> {
  return get('/v1/orders/admin/compensation', true);
}

/**
 * PUT /v1/orders/admin/compensation/:clinicianRef → set one clinician's six
 * pence rates (ADMIN). All six always ride the wire: the server's newer keys
 * are optional-preserve for one deploy window (absent = leave unchanged), and
 * sending the complete card is what makes a rate save unambiguous.
 */
export function setCompensation(clinicianRef: string, rates: CompensationRates): Promise<ClinicianCompensationRow> {
  return put(`/v1/orders/admin/compensation/${encodeURIComponent(clinicianRef)}`, rates);
}

/**
 * GET /v1/orders/admin/earnings → per-clinician earnings = counts × rates
 * (ADMIN money). `from`/`to` are the month picker's half-open UTC instants
 * (lib/monthRange) — the old `?days` window retired with the picker
 * (2026-08-26).
 */
export function getEarnings(range: { from: string; to: string }): Promise<EarningsResponse> {
  return get(`/v1/orders/admin/earnings${queryString(range)}`, true);
}

/**
 * GET /v1/orders/clinician/earnings/me → the signed-in clinician's OWN
 * earnings for the window (CTO 2026-08-26 — own compensation is visible;
 * patient money and other clinicians stay ADMIN-only). R41: the server
 * derives the clinician from the session — no ref ever rides the request.
 */
export function getMyEarnings(range: { from: string; to: string }): Promise<MyEarningsResponse> {
  return get(`/v1/orders/clinician/earnings/me${queryString(range)}`, true);
}

/**
 * POST /v1/orders/admin/payments/:paymentRecordId/refund → refund a CAPTURED
 * payment (ADMIN money reversal). `reason` (3..2000) is required and lands on
 * the server-side audit row ONLY (R19). Only a captured payment is refundable
 * (the server 400s a held/released one, 409s an already-refunded one).
 */
export function refundPayment(paymentRecordId: string, reason: string): Promise<{ paymentRecordId: string; phase: string }> {
  return post(`/v1/orders/admin/payments/${encodeURIComponent(paymentRecordId)}/refund`, { reason }, { auth: true });
}

/**
 * GET /v1/orders/admin/subscriptions/:id → the subscription drill-down (order
 * + payments + prescriptions + pharmacy sends) the "Orders & subscriptions"
 * tab's row-click Drawer reads (ADMIN, v0.6.3).
 */
export function getSubscriptionDetail(subscriptionId: string): Promise<StaffSubscriptionDetail> {
  return get(`/v1/orders/admin/subscriptions/${encodeURIComponent(subscriptionId)}`, true);
}

/**
 * POST /v1/orders/subscriptions/:id/cancel → THE PATIENT'S OWN stop (2026-08-28).
 *
 * Term-honouring, and that is the whole difference from `cancelSubscription`
 * below. This sets cancel-at-term-end: the months the patient has already been
 * billed for stay theirs and keep dispensing, and the months ahead never bill.
 * The ADMIN `cancel-now` stops billing outright and is a back-office action for
 * someone who phones in — never wire a patient control to it.
 *
 * Owner-scoped and tenant-scoped server-side: the endpoint refuses a
 * subscription belonging to anyone else, so the id on the dashboard is not a
 * capability. Idempotent — a double press returns the same answer and emits no
 * second churn event.
 *
 * ⚠️ THIS IS WHAT MAKES THE 3-MONTH LOCK FAIR. Checkout tells the patient they
 * can stop future months from their account; this call is that promise. If it
 * is removed, the lock in checkout/PlanSelection must go with it.
 */
export function stopFuturePlanMonths(
  subscriptionId: string,
): Promise<{ subscriptionId: string; state: string; cancelAtTermEnd: boolean; monthsRemaining: number }> {
  return post(`/v1/orders/subscriptions/${encodeURIComponent(subscriptionId)}/cancel`, {}, { auth: true });
}

/**
 * POST /v1/orders/subscriptions/:id/cancel-now → ADMIN immediate cancel: stops
 * ALL future billing from the next cycle (state → cancelled, nextBillingAt
 * cleared) with NO refund — the back-office action for a patient who phones in
 * to cancel. Distinct from a patient's own term-honouring cancel; audited
 * server-side (subscription_cancelled). Idempotent: an already-terminal plan
 * comes back unchanged.
 */
export function cancelSubscription(subscriptionId: string): Promise<{ subscriptionId: string; state: string }> {
  return post(`/v1/orders/subscriptions/${encodeURIComponent(subscriptionId)}/cancel-now`, {}, { auth: true });
}

/**
 * GET /v1/orders/admin/audit?limit&before → the orders-service audit trail
 * (ADMIN, G2): order approvals/rejections, prescription issues, consult
 * charges — newest first; `before` pages strictly older entries. The Wave-3c
 * tracer filters (actorUserId / action / targetType / from / to) ride the
 * same query — comma-lists validated STRICTLY against the orders vocabulary
 * server-side, so callers intersect first (admin/auditVocab.ts). Wave 4 §3: a
 * CLINICIAN caller may read too, scoped to `actorUserId=self` AND the
 * money-free clinical actions — the My-stats "My activity log" panel sends
 * both filters explicitly (never `payment_refunded`/`compensation_updated`).
 */
export function getOrdersAudit(params: AuditQuery = {}): Promise<AuditListResponse> {
  return get(`/v1/orders/admin/audit${queryString(params)}`, true);
}

/* ── Per-case clinician notes (CLINICIAN / ADMIN, G3 — pinned wire shape) ─────
 *
 * The orders-service mirrors clinic-service's CallbackNote pattern onto the
 * OrderCase: a note thread a clinician (or admin) keeps on a case INDEPENDENT
 * of the prescribe/reject decision. The gateway maps `/api/v1/orders/*` to the
 * orders-service, so the portal path for the pinned `/v1/order-cases/:id/notes`
 * base is `/v1/orders/order-cases/:id/notes`. Bodies are server-sanitised; the
 * portal render applies a second defence-in-depth strip (lib/sanitizeHtml).
 */

/** GET /v1/orders/order-cases/:id/notes → the case's note thread, oldest→newest. */
export function getCaseNotes(orderCaseId: string): Promise<CaseNoteList> {
  return get(`/v1/orders/order-cases/${encodeURIComponent(orderCaseId)}/notes`, true);
}

/** POST /v1/orders/order-cases/:id/notes → add a note (author = current user, server-sanitised). */
export function addCaseNote(orderCaseId: string, body: string): Promise<CaseNote> {
  return post(`/v1/orders/order-cases/${encodeURIComponent(orderCaseId)}/notes`, { body }, { auth: true });
}

/** PATCH /v1/orders/order-cases/:id/notes/:noteId → edit YOUR OWN note (the server re-checks authorship; stamps editedAt). */
export function editCaseNote(orderCaseId: string, noteId: string, body: string): Promise<CaseNote> {
  return patch(`/v1/orders/order-cases/${encodeURIComponent(orderCaseId)}/notes/${encodeURIComponent(noteId)}`, { body });
}

/**
 * POST /v1/orders/admin/patients/treatment-status — the money-free treatment
 * column for the staff patient directory (orders #186).
 *
 * A POST FOR A READ, deliberately: the body carries patient ids, and a query
 * string is written verbatim into the GCLB access log, which this estate keeps
 * for 400 days. Same reasoning as the deal search.
 *
 * ⚠️ THE ROLE SPLIT IS THE SERVER'S, NOT OURS. A CLINICIAN response has no
 * `planState` KEY AT ALL — orders builds a `.strict()` shape without one from
 * the gateway-verified role. So this call is safe to make from either console
 * and the clinician one simply cannot receive plan detail. Do not add a
 * client-side role check here and imagine it is the control.
 *
 * Capped at 100 ids server-side; the directory pages at 25.
 */
export function getPatientTreatmentStatus(
  userIds: readonly string[],
): Promise<import('./types').PatientTreatmentStatusResponse> {
  return post('/v1/orders/admin/patients/treatment-status', { userIds: [...userIds] }, { auth: true });
}


/**
 * GET /v1/orders/admin/patients/:patientUserId/context → the per-patient
 * money aggregate (UX Wave 6 §5 — the PMS record's "Subscriptions & payments"
 * tab): every order case, subscription, payment-ledger row and prescription
 * for one patient, plus totals. ADMIN-ONLY money surface (the route 403s a
 * CLINICIAN); the clinician PMS variant never calls this — asserted by the
 * money-redaction sweep.
 */
export function getPatientContext(patientUserId: string): Promise<import('./types').PatientOrdersContext> {
  return get(`/v1/orders/admin/patients/${encodeURIComponent(patientUserId)}/context`, true);
}

/* ── Key alerts: the double-prescribing safety check (2026-08-07) ────────────
 *
 * The pinned orders-service route is `GET /v1/patients/:patientUserId/
 * medication-safety`; the gateway maps `/api/v1/orders/*` onto the
 * orders-service, so the PORTAL path carries the extra `orders` segment —
 * the same translation the case-note routes above document.
 */

/**
 * GET /v1/orders/patients/:patientUserId/medication-safety (CLINICIAN/ADMIN)
 * → what this patient is already prescribed. MONEY-FREE for both roles.
 *
 * `excludeOrderCaseId` drops the case being reviewed from the answer, so the
 * order in front of the clinician never alerts against itself.
 *
 * Deliberately NOT called on any patient surface: the patient lane is never
 * blocked and never told (a "you already have medication" message to a patient
 * is a clinical conversation, not a UI string).
 */
export function getMedicationSafety(
  patientUserId: string,
  excludeOrderCaseId?: string,
): Promise<import('./types').MedicationSafetyResponse> {
  return get(
    `/v1/orders/patients/${encodeURIComponent(patientUserId)}/medication-safety${queryString({ excludeOrderCaseId })}`,
    true,
  );
}

/* ── End-of-term re-subscribe (v0.8.7) ───────────────────────────────────────
 *
 * The orders-service pins these on `/v1/resubscribe/*`; the gateway maps
 * `/api/v1/orders/*` onto the service, so every PORTAL path carries the extra
 * `orders` segment — the same translation the case-note and medication-safety
 * routes above document.
 *
 * THREE AUDIENCES ON ONE PREFIX, and the difference is the guard, not the path:
 * `/offers*` and `/self` are authed (ADMIN and PATIENT respectively, both
 * enforced server-side against the verified principal — never a body field),
 * while `/preview`, `/confirm` and `/decline` are PUBLIC and carry the emailed
 * capability token.
 *
 * THE TOKEN ALWAYS TRAVELS IN THE BODY, NEVER IN A URL. A query string is
 * written verbatim into load-balancer, WAF and nginx access logs on the first
 * request and no client-side rewrite retracts it — and the gateway 400s the
 * lane if a query string is present at all. That is also why the three reads
 * are POSTs rather than GETs.
 */

/**
 * POST /v1/orders/resubscribe/offers (ADMIN) → start a re-subscribe for a
 * finished plan.
 *
 * LANE B (`patient_confirmed`, the default) creates a `pending_confirmation`
 * offer and emails the patient the confirmation link — nothing is authorised
 * here. LANE A (`admin_authorised`) carries the recorded authorisation and
 * comes back already `confirmed` (or `payment_action_required`), because the
 * admin took the patient's yes on the call.
 *
 * THE TWO LANES ANSWER WITH DIFFERENT SHAPES, which is why the return type is a
 * union. Lane B hands back the full offer view; lane A runs the confirm inline
 * and hands back the CONFIRM RESULT — ids, state and failure reason, but no
 * `lane`, `months`, `monthlyPence` or authorisation read-back. Narrow with
 * {@link isResubscribeOffer} before reading any offer-only field.
 *
 * `reason` is required and lands on the append-only audit row only (R19).
 */
export function initiateResubscribe(
  body: ResubscribeInitiateRequest,
): Promise<ResubscribeInitiateResult> {
  return post('/v1/orders/resubscribe/offers', body, { auth: true, parseError: true });
}

/**
 * Narrow a `POST /offers` answer to the full offer view.
 *
 * `lane` is the discriminator rather than `state`, because both shapes carry a
 * state and only the offer view carries a lane. Lives here rather than in
 * `types.ts`, which is deliberately types-only.
 */
export function isResubscribeOffer(result: ResubscribeInitiateResult): result is ResubscribeOffer {
  return 'lane' in result;
}

/**
 * GET /v1/orders/resubscribe/offers (ADMIN) → the offers page, newest first.
 * Bounded by `limit` with a `before` cursor (the flood-defence rule — no
 * unbounded staff list), optionally filtered to one offer state.
 */
export function listResubscribeOffers(
  params: { state?: ResubscribeOfferState; limit?: number; before?: string } = {},
): Promise<ResubscribeOfferList> {
  return get(`/v1/orders/resubscribe/offers${queryString(params)}`, true);
}

/**
 * POST /v1/orders/resubscribe/offers/:offerId/withdraw (ADMIN) → cancel a live
 * offer. The REQUIRED `reason` is recorded on the audit row naming the admin as
 * actor, exactly like a refund. The new order case and its dormant subscription
 * are cancelled in the same transaction, which is what re-opens eligibility for
 * a future offer.
 */
export function withdrawResubscribeOffer(offerId: string, reason: string): Promise<ResubscribeOffer> {
  return post(`/v1/orders/resubscribe/offers/${encodeURIComponent(offerId)}/withdraw`, { reason }, {
    auth: true,
    parseError: true,
  });
}

/**
 * POST /v1/orders/resubscribe/self (PATIENT) → the signed-in patient starts
 * their own new term. Created AND confirmed in one request (no token, no
 * email): the session IS the authorisation, so there is nobody else to ask.
 * Ownership is checked server-side against the verified principal.
 */
export function resubscribeSelf(body: ResubscribeSelfRequest): Promise<ResubscribeConfirmResult> {
  return post('/v1/orders/resubscribe/self', body, { auth: true, parseError: true });
}

/**
 * POST /v1/orders/resubscribe/preview (PUBLIC) → what the emailed link is
 * offering: treatment, term, price and expiry.
 *
 * Every failure — unknown, expired, already used, withdrawn, wrong state — is
 * a byte-identical 404, so this is not an enumeration oracle. Callers must
 * render ONE neutral "this link is no longer valid" state for all of them and
 * never try to tell them apart.
 */
export function previewResubscribe(token: string): Promise<ResubscribeOfferPreview> {
  return post('/v1/orders/resubscribe/preview', { token } satisfies ResubscribeTokenRequest, {});
}

/**
 * POST /v1/orders/resubscribe/confirm (PUBLIC) → the patient authorises the new
 * term. Places an off-session HOLD on the vaulted card; it does NOT capture.
 * Capture happens only inside the clinician's approval of the NEW order case,
 * against a NEW approval — the prior term's approval is not reachable from it.
 *
 * A `payment_action_required` result is an honest stall (SCA, a declined card
 * or no vaulted card), not a failure: it carries a `paymentLinkUrl` to finish
 * with any card. Same 404 discipline as the preview.
 */
export function confirmResubscribe(token: string): Promise<ResubscribeConfirmResult> {
  return post('/v1/orders/resubscribe/confirm', { token } satisfies ResubscribeTokenRequest, {});
}

/**
 * POST /v1/orders/resubscribe/decline (PUBLIC) → "no thanks". An explicit
 * decline is worth having: it is an honest churn signal rather than a silent
 * 14-day timeout, and it releases the offer lock immediately so the admin can
 * start a fresh conversation.
 */
export function declineResubscribe(token: string): Promise<ResubscribeDeclineResult> {
  return post('/v1/orders/resubscribe/decline', { token } satisfies ResubscribeTokenRequest, {});
}

/**
 * POST /v1/orders/resubscribe/expire-due (SCHEDULER/ADMIN) → sweep offers past
 * their TTL. Counts only. Exposed here for completeness of the client surface;
 * production runs it from Cloud Scheduler, not from a browser.
 */
export function expireDueResubscribeOffers(): Promise<{ expired: number }> {
  return post('/v1/orders/resubscribe/expire-due', {}, { auth: true });
}
